import { buildConePolygon, buildRangeRing, computeBearing, destinationPoint } from "../helpers/monitoringZone"
import * as L from "leaflet"
import { nearbyShipPopup } from "./nearbyShipPopup"
import { fixAntimeridian } from "modules/MapComponent/components/map/positions"
import moment from "moment"
import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  CircleMarker,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet"
import { VesselStay } from "../hooks/useFetchVesselStays"
import { VesselPositions } from "../hooks/useFetchVesselPositions"
import { NearbyShipsResponse } from "../hooks/usePrevetAPI"
import { getEnv } from "../../../config"

interface PrevetModalMapProps {
  vesselStays: VesselStay[]
  vesselPositions: VesselPositions[]
  showPortStayMarkers: boolean
  showTradingPatternMarkers: boolean
  dateForCenterMarker: string | undefined
  timeWindow: { label: string; value: number }
  nearbyShipsData?: NearbyShipsResponse
  chartLadenStatus?: boolean
  segmentName?: string
}

const NM_100_METERS = 185200
const HEADING_LINE_NM = 4
const HEADING_LINE_METERS = HEADING_LINE_NM * 1852
const CROSSHAIR_METERS = 10000

const ZONE_ACTIVE = { primary: '#00E5FF', fillAlpha: 0.04, stripeAlpha: 0.10, ringAlpha: 0.12, glowAlpha: 0.25 }
const ZONE_STANDBY = { primary: '#90A4AE', fillAlpha: 0.03, stripeAlpha: 0.07, ringAlpha: 0.08, glowAlpha: 0.12 }

function createArrowIcon(calcCog: number): L.DivIcon {
  return L.divIcon({
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    html: `<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"
      style="transform: rotate(${calcCog}deg)">
      <defs>
        <linearGradient id="shipGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#ff4444"/>
          <stop offset="100%" stop-color="#991111"/>
        </linearGradient>
      </defs>
      <polygon points="16,3 24,26 16,20 8,26" fill="url(#shipGrad)" stroke="#cc2222" stroke-width="1.5"/>
    </svg>`,
  })
}

// Centers the map on the focus vessel when the modal first opens.
function FlyToFocus({ vesselCenter }: { vesselCenter: [number, number] }) {
  const map = useMap()
  const hasFlewRef = useRef(false)

  useEffect(() => {
    if (hasFlewRef.current || !map) return
    hasFlewRef.current = true
    map.setView(vesselCenter, 6)
  }, [map, vesselCenter])

  return null
}

// Shift longitude into the same world copy the map is currently viewing.
// fixAntimeridian mutates vessel positions, which can shift the map center
// to a wrapped world (e.g. lng 480 instead of 120). Without this, layers
// using the API's original coordinates end up 8192px off-screen.
function alignLngToMapCenter(lng: number, mapCenterLng: number): number {
  while (lng - mapCenterLng > 180) lng -= 360
  while (mapCenterLng - lng > 180) lng += 360
  return lng
}

interface NearbyShipsLayerProps {
  chartLadenStatus?: boolean
  nearbyShipsData?: NearbyShipsResponse
  vesselPositions: VesselPositions[]
  chosenDataPoint?: VesselPositions
  segmentName?: string
}

export function NearbyShipsLayer({ nearbyShipsData, vesselPositions, chosenDataPoint, segmentName, chartLadenStatus }: NearbyShipsLayerProps) {
  const map = useMap()
  const layersRef = useRef<L.Layer[]>([])

  // Inject CSS animations for monitoring zone radar effects
  useEffect(() => {
    const styleId = 'monitoring-zone-animations'
    if (document.getElementById(styleId)) return
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = [
      '@keyframes mz-dash { to { stroke-dashoffset: -24; } }',
      '@keyframes mz-glow { 0%,100% { opacity: 0.15; } 50% { opacity: 0.4; } }',
      '.mz-dash-animate { animation: mz-dash 4s linear infinite; }',
      '.mz-glow-pulse { animation: mz-glow 3s ease-in-out infinite; }',
    ].join('\n')
    document.head.appendChild(style)
    return () => { document.getElementById(styleId)?.remove() }
  }, [])

  useEffect(() => {
    const focusShip = nearbyShipsData?.focus
    const nearbyShips = nearbyShipsData?.nearby ?? []

    let focusLat: number | undefined
    let focusLon: number | undefined
    let focusHeading: number | undefined

    if (focusShip) {
      focusLat = focusShip.lat
      focusLon = focusShip.lon
      focusHeading = focusShip.calc_cog
    } else if (chosenDataPoint && nearbyShipsData) {
      focusLat = chosenDataPoint.lat
      focusLon = chosenDataPoint.lon
      // Estimate heading from the chronologically next position in the track
      const idx = vesselPositions.indexOf(chosenDataPoint)
      if (idx >= 0) {
        const neighbors: VesselPositions[] = []
        if (idx > 0) neighbors.push(vesselPositions[idx - 1])
        if (idx < vesselPositions.length - 1) neighbors.push(vesselPositions[idx + 1])

        const currTime = new Date(chosenDataPoint.pos_date).getTime()
        const laterPt = neighbors.find(p => new Date(p.pos_date).getTime() > currTime)
        const earlierPt = neighbors.find(p => new Date(p.pos_date).getTime() <= currTime)

        if (earlierPt) {
          focusHeading = computeBearing(earlierPt.lat, earlierPt.lon, chosenDataPoint.lat, chosenDataPoint.lon)
        } else if (laterPt) {
          focusHeading = computeBearing(chosenDataPoint.lat, chosenDataPoint.lon, laterPt.lat, laterPt.lon)
        }
      }
    }

    if (focusLat == null || focusLon == null || !map) return

    const removeLayers = () => {
      layersRef.current.forEach(layer => { if (map.hasLayer(layer)) layer.remove() })
      layersRef.current = []
    }

    removeLayers()

    const hasNearbyShips = nearbyShips.length > 0
    const zone = hasNearbyShips ? ZONE_ACTIVE : ZONE_STANDBY

    const createLayers = () => {
      removeLayers()

      const mapCenterLng = map.getCenter().lng
      const adjustedLon = alignLngToMapCenter(focusLon!, mapCenterLng)
      const center: [number, number] = [focusLat!, adjustedLon]

      // Inject scan-line SVG pattern into the map's renderer
      const renderer = (map as any)._renderer
      if (renderer?._container) {
        const svg = renderer._container as SVGSVGElement
        let defs = svg.querySelector('#mz-defs') as SVGDefsElement | null
        if (!defs) {
          defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
          defs.id = 'mz-defs'
          svg.insertBefore(defs, svg.firstChild)
        }
        defs.innerHTML = ''
        const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern')
        pattern.id = 'mz-scanlines'
        pattern.setAttribute('patternUnits', 'userSpaceOnUse')
        pattern.setAttribute('width', '100')
        pattern.setAttribute('height', '4')
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
        rect.setAttribute('width', '100')
        rect.setAttribute('height', '1.5')
        rect.setAttribute('fill', zone.primary)
        rect.setAttribute('fill-opacity', String(zone.stripeAlpha))
        pattern.appendChild(rect)
        defs.appendChild(pattern)
      }

      // Cardinal grid lines (N/S/E/W radial lines for orientation)
      ;[0, 90, 180, 270].forEach(bearing => {
        const endpoint = destinationPoint(focusLat!, adjustedLon, bearing, NM_100_METERS)
        const gridLine = L.polyline([center, endpoint], {
          color: zone.primary, weight: 0.5, dashArray: '2 6', opacity: zone.ringAlpha,
          interactive: false,
        }).addTo(map)
        layersRef.current.push(gridLine)
      })

      // Range rings at 75, 50, 25 NM
      ;[0.75, 0.5, 0.25].forEach(ratio => {
        const ring = L.polyline(buildRangeRing(focusLat!, adjustedLon, NM_100_METERS * ratio), {
          color: zone.primary, weight: 0.5, dashArray: '3 5',
          opacity: zone.ringAlpha, fillOpacity: 0, interactive: false,
        }).addTo(map)
        layersRef.current.push(ring)
      })

      // Pulsing glow ring behind the boundary
      const glowCircle = L.polyline(buildRangeRing(focusLat!, adjustedLon, NM_100_METERS), {
        color: zone.primary, weight: 6, opacity: zone.glowAlpha,
        fillOpacity: 0, className: 'mz-glow-pulse', interactive: false,
      }).addTo(map)
      layersRef.current.push(glowCircle)

      // Main 100 NM boundary circle with animated dash
      const boundaryCircle = L.polyline(buildRangeRing(focusLat!, adjustedLon, NM_100_METERS), {
        color: zone.primary, weight: 1.2, dashArray: '8 4',
        opacity: 0.7, fillOpacity: 0, className: 'mz-dash-animate',
        interactive: false,
      }).addTo(map)
      layersRef.current.push(boundaryCircle)

      // Heading cone with scan-line fill
      if (focusHeading != null) {
        const coneVertices = buildConePolygon(focusLat!, adjustedLon, focusHeading, NM_100_METERS, 45)

        // Subtle solid base fill
        const coneFill = L.polygon(coneVertices, {
          stroke: false,
          fillColor: zone.primary, fillOpacity: zone.fillAlpha,
          interactive: false,
        }).addTo(map)
        layersRef.current.push(coneFill)

        // Scan-line overlay + border
        const coneScanline = L.polygon(coneVertices, {
          color: zone.primary, weight: 1.2, opacity: 0.5,
          fillColor: 'url(#mz-scanlines)' as any, fillOpacity: 1,
          interactive: false,
        }).addTo(map)
        layersRef.current.push(coneScanline)
      }

      // Center crosshair
      const crossN = destinationPoint(focusLat!, adjustedLon, 0, CROSSHAIR_METERS)
      const crossS = destinationPoint(focusLat!, adjustedLon, 180, CROSSHAIR_METERS)
      const crossE = destinationPoint(focusLat!, adjustedLon, 90, CROSSHAIR_METERS)
      const crossW = destinationPoint(focusLat!, adjustedLon, 270, CROSSHAIR_METERS)
      const nsLine = L.polyline([crossN, crossS], {
        color: zone.primary, weight: 0.8, opacity: 0.4, interactive: false,
      }).addTo(map)
      const ewLine = L.polyline([crossE, crossW], {
        color: zone.primary, weight: 0.8, opacity: 0.4, interactive: false,
      }).addTo(map)
      layersRef.current.push(nsLine, ewLine)

      // Nearby ship markers with heading lines
      nearbyShips.forEach(ship => {
        const shipLon = alignLngToMapCenter(ship.lon, mapCenterLng)

        const shipMarker = L.marker([ship.lat, shipLon], { icon: createArrowIcon(ship.calc_cog) })
          .bindPopup(
            nearbyShipPopup(ship,
              chartLadenStatus !== undefined
                ? chartLadenStatus ? "laden" : "ballast"
                : chosenDataPoint ? getMarkerLadenOrBallastStatus(chosenDataPoint) : undefined,
              segmentName)
          )
          .on("mouseover", (e: any) => e.target.openPopup())
          .on("mouseout", (e: any) => e.target.closePopup())
          .addTo(map)
        layersRef.current.push(shipMarker)

        // Trail line extends behind the ship (opposite its heading)
        const headingEnd = destinationPoint(ship.lat, shipLon, ship.calc_cog + 180, HEADING_LINE_METERS)
        const headingLine = L.polyline([[ship.lat, shipLon], headingEnd], {
          color: '#FF8A80', weight: 1.5, opacity: 0.5, interactive: false,
        }).addTo(map)
        layersRef.current.push(headingLine)
      })
    }

    // Delay to let modal animation finish and map container settle
    const timer = setTimeout(createLayers, 500)
    return () => {
      clearTimeout(timer)
      removeLayers()
    }
  }, [nearbyShipsData, vesselPositions, chosenDataPoint, segmentName, chartLadenStatus, map])

  return null
}

const MAPTILER_KEY = getEnv("REACT_APP_MAPTILER_KEY")

const HYBRID_MAP_URL = `https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`


function getMarkerColor(duration: any): string {
  if (duration >= 20) {
    return "#ec4561"
  }
  if (duration >= 10 && duration <= 19) {
    return "#ffdd15"
  }
  if (duration <= 9) {
    return "#02a499"
  }
  return "FFFFFF"
}

/**
 * Resolve the AIS position the map should highlight.
 *
 * The speed chart is built from vessel legs while the map is built from daily AIS
 * positions, and the two sets do not overlap perfectly: the positions endpoint
 * drops any day with SOG below 5 kn, which is exactly the kind of slow leg the
 * zones of concern invite the user to click. Falling back to the first position of
 * the window (the previous behaviour) put the marker months away from the clicked
 * date, so pick the closest available day instead.
 */
function findPositionForDate(
  vesselPositions: VesselPositions[],
  requestedDate: string | undefined
): VesselPositions | undefined {
  if (vesselPositions.length < 1) {
    return undefined
  }
  if (!requestedDate) {
    return vesselPositions[0]
  }

  const exactMatch = vesselPositions.find(
    (position) => position.pos_date === requestedDate
  )
  if (exactMatch) {
    return exactMatch
  }

  const target = moment(requestedDate, "YYYY-MM-DD")
  if (!target.isValid()) {
    return vesselPositions[0]
  }

  return vesselPositions.reduce((closest, position) => {
    const closestDiff = Math.abs(
      moment(closest.pos_date, "YYYY-MM-DD").diff(target, "days")
    )
    const positionDiff = Math.abs(
      moment(position.pos_date, "YYYY-MM-DD").diff(target, "days")
    )
    return positionDiff < closestDiff ? position : closest
  })
}

function getCenterMarker(
  vesselPositions: VesselPositions[],
  chosenDataPoint: VesselPositions | undefined
) {
  if (vesselPositions.length < 1) {
    return [51.505, -0.09]
  }
  if (chosenDataPoint) {
    return [chosenDataPoint.lat, chosenDataPoint.lon]
  }
  return [vesselPositions[0].lat, vesselPositions[0].lon]
}

function renderPopup(
  marker: VesselPositions,
  markerColor: string,
  ladenStatus: string
) {
  return (
    <>
      {moment(marker.pos_date).format("DD-MM-YYYY")} <br />
      Speed : {marker.sog} (Kt) <br />
      <div style={{ color: `${markerColor}`, fontWeight: "bold" }}>
        {ladenStatus.toUpperCase()}
      </div>
      Destination : {marker.master_destination} <br />
      Draft Ratio : {marker.draft_ratio} <br />
      ETA : {marker.master_eta} <br />
      {marker.margin && (
        <div
          className={`text-${
            marker.margin * 100 > 10
              ? "danger"
              : marker.margin * 100 > 6
              ? "warning"
              : "success"
          }`}
        >
          Sea Margin:{" "}
          <span className="text-black fw-bold">
            {(marker.margin * 100).toFixed(2)}%
          </span>
        </div>
      )}
    </>
  )
}

type MarkerLadenOrBallastStatus = "laden" | "ballast"

function getMarkerLadenOrBallastStatus(
  marker: VesselPositions
): MarkerLadenOrBallastStatus {
  if (
    typeof marker.model_laden === "undefined" ||
    typeof marker.draft_laden === "undefined"
  ) {
    return marker.draft_ratio > 0.7 ? "laden" : "ballast"
  }

  if (marker.model_laden === true) {
    return "laden"
  }

  if (marker.model_laden === false) {
    return "ballast"
  }

  if (marker.model_laden === null && marker.draft_laden === true) {
    return "laden"
  }

  if (marker.model_laden === null && marker.draft_laden === false) {
    return "ballast"
  }

  return marker.draft_ratio > 0.7 ? "laden" : "ballast"
}

function getMarkerActivityForPortStay(activity: string) {
  if (activity === "L") {
    return "Loading"
  }
  if (activity === "D") {
    return "Discharging"
  }
  if (activity === "F") {
    return "Bunkering"
  }
}

export function PrevetModalMap({
  vesselStays,
  vesselPositions,
  showPortStayMarkers,
  showTradingPatternMarkers,
  dateForCenterMarker,
  timeWindow,
  nearbyShipsData,
  chartLadenStatus,
  segmentName,
}: PrevetModalMapProps) {
  const [fixedVesselPositions, setFixedVesselPositions] = useState<
    VesselPositions[]
  >([])

  useEffect(() => {
    setFixedVesselPositions(fixAntimeridian(vesselPositions))
  }, [vesselPositions])

  const [mapReady, setMapReady] = useState<boolean>(false)

  const chosenDataPoint = useMemo(
    () => findPositionForDate(vesselPositions ?? [], dateForCenterMarker),
    [vesselPositions, dateForCenterMarker]
  )

  let chosenDataPointPopupRef = useRef<null | L.Popup>(null)

  const [chosenDataPointPopupRefReady, setChosenDataPointPopupRefReady] =
    useState(false)

  const centerMarker =
    vesselPositions && vesselPositions.length > 0
      ? chosenDataPoint
        ? [chosenDataPoint.lat, chosenDataPoint.lon]
        : nearbyShipsData?.focus
          ? [nearbyShipsData.focus.lat, nearbyShipsData.focus.lon]
          : getCenterMarker(vesselPositions, chosenDataPoint)
      : [51.505, -0.09]
  const [portStayData, setPortStayData] = useState<VesselStay[]>(vesselStays)

  useEffect(() => {
    const currentDate = new Date()
    const pastDate = new Date()
    pastDate.setMonth(currentDate.getMonth() - timeWindow.value)
    const filteredPortStay = vesselStays.filter(
      (stay) => new Date(stay.date_from) > pastDate
    )
    setPortStayData(filteredPortStay)
  }, [timeWindow])

  // Need to use vessel-stay data instead
  /* const portStayData = vesselStays */
  const tradingPolyLine: any = []
  const portStayMarkers =
    showPortStayMarkers && portStayData.length > 0
      ? portStayData.map((marker: any) => {
          const markerColor = getMarkerColor(marker.duration)

          return (
            <CircleMarker
              key={
                "port-" +
                marker.port +
                "-" +
                marker.date_from +
                "-" +
                marker.duration
              }
              center={{ lat: marker.lat, lng: marker.lon }}
              radius={10}
              fillOpacity={0.9}
              stroke={false}
              fillColor={markerColor}
              eventHandlers={{
                mouseover: (event: any) => event.target.openPopup(),
                mouseout: (event: any) => event.target.closePopup(),
              }}
            >
              <Popup>
                <div>Arrival: {marker.date_from}</div>
                <div>Departure: {marker.date_to}</div>
                <div className="d-flex align-items-center">
                  <span>Duration :</span>
                  <span className="ms-1 me-1">
                    {getColorCode(marker.duration)}
                  </span>
                  <span>{marker.duration} Days</span>
                  <span className="ms-1">{"("}</span>
                  <span>{getPortStayDurationText(marker.duration)}</span>
                  <span>{")"}</span>
                </div>
                <div>Port : {marker.port}</div>
                <div>
                  Activity : {getMarkerActivityForPortStay(marker.activity)}
                </div>
              </Popup>
            </CircleMarker>
          )
        })
      : null

  const tradingPatternMarkers = showTradingPatternMarkers
    ? vesselPositions.map((marker: VesselPositions, index) => {
        tradingPolyLine.push([marker.lat, marker.lon])

        const ladenStatus = getMarkerLadenOrBallastStatus(marker)
        const markerColor = ladenStatus === "laden" ? "#3050A2" : "#FF5B29"

        const userHasSelectedDataPoint =
          chosenDataPoint?.pos_date === marker.pos_date

        if (userHasSelectedDataPoint) {
          const selectedLadenStatus: MarkerLadenOrBallastStatus =
            chartLadenStatus !== undefined
              ? chartLadenStatus ? "laden" : "ballast"
              : ladenStatus
          const selectedColor = selectedLadenStatus === "laden" ? "#3050A2" : "#FF5B29"

          return (
            <Marker
              key={"position-" + marker.pos_date}
              eventHandlers={{
                mouseover: (event: any) => event.target.openPopup(),
                // load: (event: any) => event.target.openPopup(),
              }}
              position={{ lat: marker.lat, lng: marker.lon }}
            >
              <Popup
                ref={(popup: L.Popup | null) => {
                  if (popup) {
                    chosenDataPointPopupRef.current = popup
                    setChosenDataPointPopupRefReady(true)
                  }
                }}
                // keepInView={true}
                closeButton={true}
              >
                {renderPopup(marker, selectedColor, selectedLadenStatus)}
              </Popup>
            </Marker>
          )
        }
        return (
          <CircleMarker
            key={"position-" + marker.pos_date}
            center={{ lat: marker.lat, lng: marker.lon }}
            radius={5}
            stroke={false}
            fillOpacity={0.6}
            fillColor={markerColor}
            eventHandlers={{
              mouseover: (event: any) => event.target.openPopup(),
              mouseout: (event: any) => event.target.closePopup(),
            }}
          >
            <Popup>{renderPopup(marker, markerColor, ladenStatus)}</Popup>
          </CircleMarker>
        )
      })
    : null

  const mapRef = useRef<L.Map | null>()
  const geodesicLines = useRef<L.Polyline[]>([])

  const createLineSegments = (): Array<{
    points: L.LatLng[]
    speed: number
  }> => {
    const segments: Array<{ points: L.LatLng[]; speed: number }> = []

    for (let i = 0; i < fixedVesselPositions.length - 1; i++) {
      const currentPoint = fixedVesselPositions[i]
      const nextPoint = fixedVesselPositions[i + 1]

      const segmentPoints = [
        new L.LatLng(currentPoint.lat, currentPoint.lon),
        new L.LatLng(nextPoint.lat, nextPoint.lon),
      ]

      segments.push({
        points: segmentPoints,
        speed: currentPoint.sog,
      })
    }

    return segments
  }

  // Effect for map initialization
  useEffect(() => {
    if (mapRef.current) {
      setMapReady(true)
    }
  }, [mapRef.current])

  // Effect for drawing lines
  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return
    }

    const drawLines = () => {
      // Clear existing lines
      geodesicLines.current.forEach((line) => {
        if (mapRef.current) {
          line.remove()
        }
      })
      geodesicLines.current = []

      // Create and add new lines
      const segments = createLineSegments()
      segments.forEach((segment) => {
        const color = getSpeedColor(segment.speed)
        const fixedPoints = segment.points
        const geodesicLine = L.polyline(fixedPoints, {
          color: color,
          weight: 2,
          dashArray: "4 4 8",
          interactive: false,
        })

        if (mapRef.current) {
          geodesicLine.addTo(mapRef.current)
          geodesicLines.current.push(geodesicLine)
        }
      })
    }

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(drawLines)

    // Cleanup function
    return () => {
      geodesicLines.current.forEach((line) => {
        if (mapRef.current) {
          line.remove()
        }
      })
      geodesicLines.current = []
    }
  }, [mapReady, fixedVesselPositions])

  useEffect(() => {
    if (chosenDataPointPopupRefReady && mapRef.current) {
      chosenDataPointPopupRef.current?.openOn(mapRef.current)
    }
  }, [chosenDataPointPopupRefReady])

  return (
    <React.Fragment>
      <MapContainer
          center={centerMarker as [number, number]}
          zoom={6}
          minZoom={3}
          maxZoom={30}
          scrollWheelZoom={true}
          style={{ height: "70vh" }}
          ref={(mapInstance) => {
            mapRef.current = mapInstance
            /* geodesicRef.current.addTo(mapInstance) */
          }}
        >
          <TileLayer
            url={HYBRID_MAP_URL}
            tileSize={512}
            zoomOffset={-1}
            minZoom={1}
            crossOrigin={true}
            maxZoom={16}
            attribution='&copy; <a href="https://www.maptiler.com/copyright/" target="_blank" rel="noreferrer">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>'
          />
          {portStayMarkers}
          {tradingPatternMarkers}
          <NearbyShipsLayer chartLadenStatus={chartLadenStatus} nearbyShipsData={nearbyShipsData} vesselPositions={vesselPositions} chosenDataPoint={chosenDataPoint} segmentName={segmentName} />
          <FlyToFocus vesselCenter={centerMarker as [number, number]} />
        </MapContainer>
    </React.Fragment>
  )
}

function getPortStayDurationText(days: number) {
  if (days < 10) {
    return <span>Short port stay</span>
  }
  if (days >= 10) {
    return <span>Prolonged port stay</span>
  }
  return <></>
}

function getColorCode(days: number) {
  const okColor = "#00763b"
  const dangerColor = "#ec4561"
  const warningColor = "#ffdd15"

  if (days < 10) {
    return (
      <div
        style={{
          borderRadius: "100%",
          height: "10px",
          width: "10px",
          backgroundColor: okColor,
        }}
      ></div>
    )
  }
  if (days >= 20) {
    return (
      <div
        style={{
          borderRadius: "100%",
          height: "10px",
          width: "10px",
          backgroundColor: dangerColor,
        }}
      ></div>
    )
  }
  if (days >= 10) {
    return (
      <div
        style={{
          borderRadius: "100%",
          height: "10px",
          width: "10px",
          backgroundColor: warningColor,
        }}
      ></div>
    )
  }
  return <></>
}

// Function to get color based on speed
const getSpeedColor = (speed: number): string => {
  if (speed >= 12) return "#02a499" // Green for 12kt+
  if (speed >= 9 && speed < 12) return "#ffdd15" // Yellow for 9-11.9kt
  return "#ec4561" // Red for 0-8.9kt
}
